import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@paysera/ui';
import { useTranslation } from 'react-i18next';
import { useGetFunctionalitiesQuery } from '@app/api/functionalitiesApi';
import { useCreateSolvencyDeclarationMutation, useFetchCurrentSolvencyDeclarationQuery, useResubmitSolvencyDeclarationMutation, } from '@app/api/solvencyDeclarationApi';
import TimeRemaining from '@app/components/alerts/TimeRemaining';
import { PageLoader } from '@app/components/PageLoader';
import { QuestionnaireSent } from '@app/components/popups';
import { FINANCING_OPTIONS } from '@app/configs/financing';
import { ROUTES } from '@app/configs/routes';
import { SOLVENCY_DECLARATION_ERRORS, SOLVENCY_DECLARATION_STATUSES } from '@app/configs/solvencyDeclaration';
import useBoolean from '@app/hooks/useBoolean';
import { getErrorMessage } from '@app/utils/getErrorMessage';
import { getUserLocale } from '@app/utils/getUserLocale';
import { isLessThan30MinutesTo22, isLocalExpirationWithin30Minutes } from '@app/utils/time';
import StatusAlerts from '@modules/borrower/components/StatusAlerts';
import { refreshExpiredModal } from '../../utils/expiredModalManager';
const ELIGIBLE_STATUSES = [
    SOLVENCY_DECLARATION_STATUSES.draft,
    SOLVENCY_DECLARATION_STATUSES.to_be_resubmitted,
    SOLVENCY_DECLARATION_STATUSES.expired,
];
const NoLoans = () => {
    var _a;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isTimeRemainingOpen, setIsTimeRemainingOpen] = useState(false);
    const { data: functionalities } = useGetFunctionalitiesQuery();
    const [createSolvencyDeclaration] = useCreateSolvencyDeclarationMutation();
    const { data: solvencyDeclaration, error, isLoading } = useFetchCurrentSolvencyDeclarationQuery();
    const [resubmitSolvencyDeclaration] = useResubmitSolvencyDeclarationMutation();
    const [isSuccessPopupOpen, { setTrue: openSuccessPopup, setFalse: closeSuccessPopup }] = useBoolean();
    const handleDismiss = () => {
        setIsTimeRemainingOpen(false);
    };
    const showTimeRemaining = () => {
        setIsTimeRemainingOpen(true);
    };
    useEffect(() => {
        const isExpirationLessThan30Minutes = isLocalExpirationWithin30Minutes(solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.expiresAt);
        const isLessThan30Minutes = isLessThan30MinutesTo22();
        if (isLessThan30Minutes || isExpirationLessThan30Minutes) {
            showTimeRemaining();
        }
    }, [solvencyDeclaration]);
    if (isLoading) {
        return <PageLoader />;
    }
    const errorMessage = getErrorMessage(error);
    const noSolvencyDeclaration = errorMessage === SOLVENCY_DECLARATION_ERRORS.current_solvency_declaration_not_found;
    const solvencyDeclarationConfig = functionalities === null || functionalities === void 0 ? void 0 : functionalities[FINANCING_OPTIONS.solvency_declaration];
    const solvencyDeclarationDisabledReason = (solvencyDeclarationConfig === null || solvencyDeclarationConfig === void 0 ? void 0 : solvencyDeclarationConfig.disabledReason) || '';
    const isServiceUnavailable = solvencyDeclarationDisabledReason.includes(SOLVENCY_DECLARATION_ERRORS.current_time_not_allowed);
    const hasError = error && errorMessage !== SOLVENCY_DECLARATION_ERRORS.current_solvency_declaration_not_found;
    const isRejectedMoreThan14Days = (solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.status) === SOLVENCY_DECLARATION_STATUSES.rejected &&
        ((_a = functionalities === null || functionalities === void 0 ? void 0 : functionalities[FINANCING_OPTIONS.solvency_declaration]) === null || _a === void 0 ? void 0 : _a.enabled);
    const hasEligibleStatus = (solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.status) && ELIGIBLE_STATUSES.includes(solvencyDeclaration.status);
    const showEvaluationButton = !isServiceUnavailable &&
        ((!hasError && (noSolvencyDeclaration || hasEligibleStatus)) || isRejectedMoreThan14Days);
    const tryAgainError = solvencyDeclarationDisabledReason.includes(SOLVENCY_DECLARATION_ERRORS.newest_solvency_declaration_rejected)
        ? solvencyDeclarationDisabledReason
        : '';
    const handleGoToLoanCreation = () => {
        navigate(ROUTES.loanApplication);
    };
    const handleGoToEvaluation = () => {
        if ((solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.status) === SOLVENCY_DECLARATION_STATUSES.to_be_resubmitted) {
            resubmitSolvencyDeclaration().then(() => {
                openSuccessPopup();
            });
        }
        else if ((solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.status) === SOLVENCY_DECLARATION_STATUSES.expired ||
            noSolvencyDeclaration ||
            isRejectedMoreThan14Days) {
            createSolvencyDeclaration().then(() => {
                refreshExpiredModal();
                navigate(ROUTES.solvencyDeclaration);
            });
        }
        else {
            navigate(ROUTES.solvencyDeclaration);
        }
    };
    const locale = getUserLocale();
    return (<div className="flex flex-col gap-6">
            {showEvaluationButton && isTimeRemainingOpen && (<div className="mb-2">
                    <TimeRemaining onDismiss={handleDismiss}/>
                </div>)}
            <StatusAlerts status={solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.status} goToLoanCreation={handleGoToLoanCreation} tryAgainError={tryAgainError} isServiceUnavailable={isServiceUnavailable} isRejectedMoreThan14Days={isRejectedMoreThan14Days}/>
            <div className="border rounded-lg border-neutral-primary-initial px-6">
                <div className="flex flex-col items-center gap-4 py-16 px-8">
                    <div className="flex flex-col items-center gap-1">
                        <div className="title-l">{t('financing.no_loans.active_loans')}</div>
                        <a href={`https://www.paysera.com/v2/${locale}/legal`} target="_blank" className="text-action-primary-initial body-l hover:underline" rel="noreferrer">
                            {t('financing.no_loans.legal_info')}
                        </a>
                    </div>
                    {showEvaluationButton && (<div>
                            <Button onClick={handleGoToEvaluation} variant="primary" label={t('financing.no_loans.request_evaluation')}/>
                        </div>)}
                </div>
            </div>
            <QuestionnaireSent isOpen={isSuccessPopupOpen} closeModal={closeSuccessPopup}/>
        </div>);
};
export default NoLoans;
